import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿Qué debo hacer si me niegan la cobertura?",
    a: "Primero averigüe cuál de los dos requisitos no cumplió. Si la negación es por «exclusión de beneficio», su plan no cubre esta categoría en absoluto y una apelación clínica no lo va a resolver; en ese caso, el pago propio es el camino realista. Si la negación es por no cumplir los criterios clínicos, muchas veces se puede corregir: envíe la documentación de intentos previos que falte, el peso y la estatura recientes, o una condición relacionada que no se haya reportado; revise si otra presentación del medicamento (pluma, KwikPen o frasco) es la que sí está cubierta; presente una apelación interna y después una revisión externa; y pídale a su médico que solicite una revisión entre colegas (peer-to-peer).",
  },
  {
    q: "¿Cuánto cuesta el medicamento para bajar de peso si pago de mi bolsillo?",
    a: "Los programas de venta directa del fabricante se han vuelto una alternativa real. Zepbound, pagado directamente a través de LillyDirect y las principales farmacias, empieza alrededor de $299 al mes para la dosis de 2.5 mg, $399 para 5 mg y $449 para dosis más altas. Wegovy, a través de NovoCare Pharmacy, empieza en $199 al mes para las dos dosis más bajas en pacientes nuevos, luego $349 al mes, y la pastilla empieza en $149 al mes. El dinero pagado por su cuenta no cuenta para su deducible ni para su máximo de gastos de bolsillo.",
  },
  {
    q: "¿Qué preguntas debo hacerle a mi aseguradora?",
    a: "Pregunte si su plan cubre medicamentos de receta para bajar de peso o si están excluidos; si Zepbound, Wegovy o Saxenda están en su formulario y en qué nivel; qué IMC exige su plan y si una condición relacionada con el peso reduce ese umbral; cuántos meses de cambios de estilo de vida documentados necesita; si debe probar otro medicamento primero; qué presentación está cubierta (pluma, KwikPen o frasco); si el mismo medicamento está cubierto para otra condición que usted tenga, como apnea del sueño, diabetes tipo 2 o enfermedad cardíaca; cuánto dura una aprobación inicial; y cuál sería su costo mensual real después del deducible.",
  },
  {
    q: "¿Las tarjetas de ahorro del fabricante tienen límites?",
    a: "Sí, dos importantes. El dinero pagado a través de programas de pago propio no cuenta para su deducible ni para su máximo de gastos de bolsillo. Y las personas con seguro del gobierno —Medicare, Medicaid, TRICARE— generalmente no pueden usar las tarjetas de descuento comerciales, aunque sí pueden pagar el precio de venta directa fuera de su seguro. Novo Nordisk aclara que FEHB, los planes del Marketplace y los planes de empleados estatales no se consideran programas del gobierno para efectos de su oferta de ahorro.",
  },
  {
    q: "Si el costo del medicamento a largo plazo es el obstáculo, ¿qué otra opción tengo?",
    a: "Vale la pena comparar contra las opciones quirúrgicas para bajar de peso, que la mayoría de los planes de Virginia sí cubren. Cirugías como la manga gástrica o el bypass gástrico ofrecen resultados duraderos sin el gasto mensual continuo de un medicamento.",
  },
];

export const Route = createFileRoute("/es/seguro/pago-propio-y-negaciones")({
  head: () => {
    const base = esHead("insuranceCashPay");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("insuranceCashPay"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/seguro/pago-propio-y-negaciones#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/seguro/pago-propio-y-negaciones#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
      ] : [],
    };
  },
  component: EsCashPayDenials,
});

function EsCashPayDenials() {
  return (
    <EsPageShell
      page="insuranceCashPay"
      title="Negaciones y opciones de pago propio"
      intro="Cómo responder a una negación de cobertura, cuánto cuesta hoy el pago directo del medicamento y exactamente qué preguntarle a su aseguradora."
    >
      <EsSection id="dos-puertas" heading="Antes de apelar">
        <p>
          Antes de apelar, confirme cuál de las dos puertas de acceso no logró pasar: la de si su
          plan cubre medicamentos para bajar de peso en absoluto, o la de si usted cumple los
          criterios clínicos. Eso determina si una apelación puede funcionar. Vea el detalle en
          nuestra página de{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            cobertura de seguro
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="negacion" heading="Si le niegan la cobertura">
        <p>
          <strong>Averigüe cuál de los dos requisitos no cumplió.</strong> Una negación por
          «exclusión de beneficio» significa que su plan no cubre esta categoría en absoluto; en ese
          caso, apelar los criterios clínicos no va a servir. Una negación por no cumplir los
          criterios clínicos muchas veces sí se puede corregir.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>Pida la negación por escrito y lea la razón específica.</strong> Las razones
            corregibles más comunes son documentación faltante de intentos previos de cambio de
            estilo de vida, peso y estatura fuera del período requerido, una condición relacionada
            que no se envió, o un requisito de terapia escalonada que no se documentó.
          </li>
          <li>
            <strong>Pregunte por la «puerta trasera».</strong> La apnea del sueño, la diabetes tipo
            2, la enfermedad cardiovascular o el MASH pueden calificarlo bajo una vía que las
            exclusiones de pérdida de peso no controlan.
          </li>
          <li>
            <strong>Revise la presentación del medicamento.</strong> Pluma, KwikPen o frasco pueden
            ser todo el problema.
          </li>
          <li>
            <strong>Presente una apelación interna y luego una revisión externa.</strong> En
            Virginia, la revisión externa está disponible a través de la Oficina de Seguros de la
            State Corporation Commission para los planes regulados por el estado; los planes de
            empleadores autofinanciados siguen reglas federales.
          </li>
          <li>
            <strong>Pida que su médico llame.</strong> Una revisión entre colegas (peer-to-peer)
            suele resolver negaciones que el papeleo por sí solo no resuelve.
          </li>
        </ul>
      </EsSection>

      <EsSection id="pago-propio" heading="Si va a pagar de su bolsillo">
        <p>
          Los programas de venta directa del fabricante se han vuelto una alternativa real, en
          particular para quienes tienen un plan que excluye estos medicamentos.
        </p>
        <p>
          <strong>Zepbound.</strong> Lilly ofrece precios de pago propio en la KwikPen a través de
          LillyDirect y, a partir de marzo de 2026, en las principales farmacias del país, empezando
          en $299 al mes para 2.5 mg, $399 para 5 mg y $449 para dosis más altas, con precios ligados
          a surtir la receta dentro de un plazo fijo.
        </p>
        <p>
          <strong>Wegovy.</strong> A través de NovoCare Pharmacy, el precio de pago propio para la
          pluma de Wegovy empieza en $199 al mes para las dos dosis más bajas en pacientes nuevos
          (una tarifa por tiempo limitado hasta el 31 de diciembre de 2026), luego $349 al mes.
          Wegovy HD cuesta $399 al mes; la pastilla de Wegovy empieza en $149 al mes para ciertas
          dosis. Los pacientes con seguro comercial pueden pagar hasta $25 al mes con la oferta de
          ahorro, sujeto a un máximo de ahorro de $100 al mes.
        </p>
        <p>
          <strong>Dos límites importantes en las tarjetas de ahorro.</strong> El dinero pagado a
          través de programas de pago propio <strong>no</strong> cuenta para su deducible ni para su
          máximo de gastos de bolsillo. Y las personas con seguro del gobierno —Medicare, Medicaid,
          TRICARE— generalmente no pueden usar las tarjetas de descuento comerciales, aunque sí
          pueden pagar el precio de venta directa fuera de su seguro. Novo Nordisk aclara que FEHB,
          los planes del Marketplace y los planes de empleados estatales no se consideran programas
          del gobierno para efectos de esta oferta.
        </p>
        <p>
          Los precios y los términos de los programas cambian con frecuencia. Confirme el precio
          actual directamente con el fabricante. Si el costo a largo plazo del medicamento es el
          obstáculo, vale la pena comparar contra las opciones quirúrgicas, que la mayoría de los
          planes de Virginia sí cubren, como la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>
          , el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>{" "}
          o la{" "}
          <Link to="/es/banda-gastrica" className="font-medium text-brand-logo hover:underline">
            banda gástrica
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="preguntas-plan" heading="Preguntas para hacerle a su plan">
        <p>Llame al número de servicios para miembros de su tarjeta y pregunte:</p>
        <ol className="list-decimal space-y-2 pl-5">
          <li>¿Mi plan cubre medicamentos de receta para bajar de peso, o están excluidos? Pida que se lo confirmen con claridad.</li>
          <li>¿Zepbound, Wegovy o Saxenda están en mi formulario, y en qué nivel?</li>
          <li>¿Qué IMC exige mi plan, y una condición relacionada con el peso reduce ese umbral?</li>
          <li>¿Cuántos meses de cambios de estilo de vida documentados necesito, y qué cuenta como documentación?</li>
          <li>¿Tengo que probar otro medicamento para bajar de peso primero?</li>
          <li>¿Qué presentación está cubierta: pluma, KwikPen o frasco?</li>
          <li>Si los medicamentos para bajar de peso están excluidos, ¿el mismo medicamento está cubierto para otra condición que yo tenga, como apnea del sueño, diabetes tipo 2, enfermedad cardíaca o hígado graso?</li>
          <li>¿Cuánto dura una aprobación inicial, y qué necesito mostrar para renovarla?</li>
          <li>¿Cuál sería mi costo mensual real después del deducible?</li>
        </ol>
      </EsSection>

      <EsSection id="ayuda" heading="Podemos ayudarlo">
        <p>
          Nuestro personal verifica beneficios y presenta autorizaciones previas cada semana, y
          sabemos qué documentación pide realmente cada plan en Virginia.{" "}
          <Link to="/es/contacto" className="font-medium text-brand-logo hover:underline">
            Contáctenos
          </Link>{" "}
          y revisamos su plan con usted.
        </p>
      </EsSection>

      <EsSection id="preguntas-frecuentes" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>
    </EsPageShell>
  );
}
